import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PinPopup from "../../components/PinPopup";
import { useAuth } from "../../services/AuthContext";
import { confirmParentPin, fetchChildren, fetchTransactions, fetchJobs, fetchJobApprovals, approveJobForChild, rejectJobForChild } from "../../services/Api";
// import LoadingSpinner from "../../components/LoadingSpinner";
import Tooltip from "../../components/Tooltip";
import AddFundsForm from "./AddFundsForm";
import SubtractFundsForm from "./SubtractFundsForm";
import AddJobsForm from "./AddJobsForm";
import AccountInfoForm from "../SettingsPages/AccountInfoForm";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useToast } from '../../services/ToastContext';
import { useLocation } from "react-router-dom";
import { useRefresh } from "../../services/RefreshContext";
import ChildActionModal from "./ChildActionModal";


// Separate Parent Dashboard Component
const ParentDash = () => {
  const location = useLocation();
  const { showToast } = useToast();
  const { refreshCounter, triggerRefresh } = useRefresh();
  const navigate = useNavigate();

  
  const [error, setError] = useState("")
  const { familyID, childID } = useAuth();
  const [children, setChildren] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobApprovals, setJobApprovals] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isSubtractFundsOpen, setIsSubtractFundsOpen] = useState(false);
  const [isAddJobsOpen, setIsAddJobsOpen] = useState(false);
  const [isAccountInfoOpen, setIsAccountInfoOpen] = useState(false);
  const [isPinPopupOpen, setIsPinPopupOpen] = useState(true);
  
  const [activeSection, setActiveSection] = useState("balances"); // default to balances
  const [selectedChildForAction, setSelectedChildForAction] = useState(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);


  const toChildDashboard = async (e) => {
    e.preventDefault();
    navigate("/selectchild");
  };

  useEffect(() => {
    if (!familyID) return;

    const fetchData = async () => {
      setIsSpinnerLoading(true);
      try {
        const minDelay = new Promise((resolve) => setTimeout(resolve, 400));
        const [childrenRes, transactionsRes, jobsRes, jobApprovalsRes] = await Promise.all([
          fetchChildren(familyID),
          fetchTransactions(familyID),
          fetchJobs(familyID),
          fetchJobApprovals(familyID),
          minDelay,
        ]);
        setChildren(childrenRes)
        setTransactions(transactionsRes.transactions);
        setJobs(jobsRes.jobs);
        setJobApprovals(jobApprovalsRes.jobApprovals);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsSpinnerLoading(false);
      }
    };

    fetchData();
  }, [familyID, isAddFundsOpen, isSubtractFundsOpen, refreshCounter]);

  const handleConfirmPin = (pin) => {
    const confirmPin = async (familyID, parentPin) => {
      try {
        await confirmParentPin(familyID, parentPin);
        setError("");
        setIsPinPopupOpen(false);
      } catch (error) {
        setError(error.message || 'Failed to confirm pin');
      }
    };
    confirmPin(familyID, pin);
  }

  const handleApproveJob = (e, job) => {
    e.preventDefault();

    const approveJob = async (job, familyID) => {
      try {
        await approveJobForChild(job, familyID);
        setError("");
        showToast(`Job Approved! Funds Added to ${job.ChildName}`);
        triggerRefresh();
      } catch (error) {
        setError(error.message || 'Failed to approve job');
      }
    };
    approveJob(job, familyID);
  };

  const handleRejectJob = (e, job) => {
    e.preventDefault();

    const rejectJob = async (job, familyID) => {
      try {
        await rejectJobForChild(job, familyID);
        setError("")
        triggerRefresh();
        showToast(`Job put back in available list`)
      } catch (error) {
        setError(error.message || 'Failed to reject job')
      }
    };
    rejectJob(job, familyID);
  };

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  const handleAddFunds = (child) => {
    setSelectedChildForAction(child);
    setIsAddFundsOpen(true);
  };
  
  const handleSubtractFunds = (child) => {
    setSelectedChildForAction(child);
    setIsSubtractFundsOpen(true);
  };

  if (isPinPopupOpen) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center">
        <i className="fas fa-lock fa-5x mt-3"></i>
        <PinPopup
          isOpen={isPinPopupOpen}
          onClose={() => { navigate("/landingpage") }}
          error={error}
          content={<>Enter pin to see the Parent Dashboard</>}
          onConfirm={handleConfirmPin}
        />
      </div>
    );
  }

  return (
    <div>
      <LoadingSpinner isLoading={isSpinnerLoading} />
      <div className={`d-flex flex-column fade-in ${!isSpinnerLoading ? "visible" : ""}`}>
        {/* Header Row */}
        {/* Top Bar */}
        {/* Header Row */}
        <div className="position-relative mb-4">
          {/* Centered Title */}
          <h1 className="h3 text-center mb-0">Grown Up Dashboard</h1>

          {/* Top-right buttons */}
          <div
            style={{
              position: "absolute",
              top: "-1rem",
              right: ".5rem",
              display: "flex",
              gap: ".1rem",
            }}
          >
            <button className="edit-btn fw-bold orange-color" onClick={toChildDashboard}>
              To Kids Dashboard
            </button>
            <button className="edit-btn fw-bold orange-color p-1" onClick={() => setIsAccountInfoOpen(true)}>
              Account Settings
            </button>
          </div>
        </div>


        {/* Tab Buttons */}
        <div className="d-flex flex-wrap mb-4" style={{ justifyContent: "center", gap: "2em" }}>
          <button
            className={`globalButton dashboard-tab-button p-3 m-2 fw-bold ${activeSection === "balances" ? "active" : ""}`}
            onClick={() => setActiveSection("balances")}
          >
            Balances
          </button>
          <button
            className={`globalButton dashboard-tab-button p-3 m-2 fw-bold ${activeSection === "jobs" ? "active" : ""}`}
            onClick={() => setActiveSection("jobs")}
          >
            Jobs
          </button>
        </div>




        {/* Content Row */}
        {activeSection === "balances" && (

          <div className="savingsTransactionsContainer d-flex">
            {/* Children Account Balances */}

            <div className="col-xl-4 col-lg-5 d-flex flex-column">
              <div className="card shadow mb-4 h-100 d-flex flex-column">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between" >
                  <h6 className="m-0 font-weight-bold">
                    Children Account Balances
                  </h6>

                  <div className="icon-buttons-container">

                    <button
                      className="icon-button"
                      onClick={() => setIsAddFundsOpen(true)}
                    >
                      <Tooltip
                        content={<i className="fas fa-plus fa-lg fa-fw mr-2 icon green" />}
                        description="Add Funds"
                        isCustom={true}
                      />
                    </button>

                    <button
                      className="icon-button"
                      onClick={() => setIsSubtractFundsOpen(true)}
                    >
                      <Tooltip
                        content={<i className="fas fa-minus fa-lg fa-fw mr-2 icon red" />}
                        description="Subtract Funds"
                        isCustom={true}
                      />
                    </button>
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      flexGrow: 1,
                      overflowY: "auto",
                      maxHeight: "25em", // adjust height as needed
                      padding: "1em 0.5em .5em 0.5em",
                      boxSizing: "border-box",
                    }}
                    className="text-center"
                  >
                    {children.map((child) => (
                      <div
                        key={child.ChildID}
                        className="kidNameDisplay center mb-2"
                        style={{ fontSize: "x-large", width: "80%", cursor: "pointer" }}
                        onClick={() => {
                          setSelectedChildForAction(child);
                          setIsActionModalOpen(true);
                        }}
                      >
                        {child.FirstName}: <br />
                        {child.Balance ? (`${child.Balance < 0 ? "-" : ""}$${Math.abs(child.Balance).toFixed(2)}`) : "$0.00"}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Card */}
            <div className="col-xl-8 col-lg-7 d-flex align-items-stretch flex-column">
              <div className="card shadow mb-4 h-100 flex-column">
                {/* Card Header */}
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                  <h6 className="m-0 font-weight-bold">All Children - Recent Transactions</h6>
                </div>

                {/* Card Body with Scrollable List */}
                <div className="card-body overflow-auto blue-color" style={{ flex: "1 1 0", minHeight: 0 }}>
                  {transactions && transactions.length !== 0 ? (
                    <>
                      {transactions.map((transaction) => (
                        <div className="transaction-item" key={transaction.ID}>
                          <div className="icon">
                            <span className={transaction.Amount > 0 ? "up" : "down"}>
                              {transaction.Amount > 0 ? "↑" : "↓"}
                            </span>
                          </div>

                          <div className="transaction-content">
                            <div className="transaction-item-margins">
                              <div className="description">
                                {transaction.ChildName ?? "Unknown Child"}
                              </div>
                            </div>

                            <div className="transaction-item-margins">
                              <div className="description">{transaction.Description}</div>
                            </div>

                            <div className="transaction-item-margins">
                              <div
                                className={`amount ${transaction.Amount > 0 ? "positive" : "negative"}`}
                              >
                                {transaction.Amount
                                  ? `${transaction.Amount < 0 ? "- " : "+ "}$${Math.abs(
                                    transaction.Amount
                                  ).toFixed(2)}`
                                  : "$0.00"}
                              </div>
                            </div>

                            <div className="transaction-item-margins">
                              <div className="date">
                                {transaction.Date
                                  ? dateFormatter.format(new Date(transaction.Date))
                                  : ""}
                              </div>
                            </div>
                          </div>
                        </div>

                      ))}
                    </>
                  ) : (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <h4 className="text-muted text-center">No transactions recorded</h4>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}









        {/*jobs stuff */}
        {activeSection === "jobs" && (

          <div className="savingsTransactionsContainer d-flex">
            {/* Children Account Balances */}

            <div className="col-xl-4 col-lg-5 d-flex flex-column align-items-stretch">
              <div className="card shadow mb-4 h-100 d-flex flex-column">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between" >
                  <h6 className="m-0 font-weight-bold">
                    Available Jobs
                  </h6>
                  <button className="globalButton p-2 fw-bold" onClick={() => setIsAddJobsOpen(true)} >
                    Add a Job
                  </button>
                </div>
                
                {jobs && jobs.length !== 0 ? (
                  <div
                  style={{
                    flexGrow: 1,
                    overflowY: "auto",
                    maxHeight: "25em",
                    padding: "1em 0.5em .5em 0.5em",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "1rem",
                      justifyContent: "center",
                    }}
                  >
                    {jobs.map((job) => (
                      <div
                        key={job.JobID}
                        style={{
                          flex: "1 1 calc(50% - 1rem)",
                          backgroundColor: "#7db4ce",
                          color: "white",
                          borderRadius: "1rem",
                          paddingTop: ".5rem",
                          // cursor: "pointer",
                          textAlign: "center",
                        }}
                      >
                        <p style={{ fontSize: "20px", fontWeight: "bold" }}>{job.JobName}</p>
                        <p>${job.PaymentAmount}<br />{job.DateCreated ? dateFormatter.format(new Date(job.DateCreated)) : ""}</p>
                      </div>
                  ))}
                  </div>
                  </div>
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <h4 className="text-muted text-center">No jobs available</h4>
                </div>
              )}
              </div>
            </div>

            <div className="col-xl-8 col-lg-7 d-flex flex-column ">
              <div className="card shadow mb-4 h-100 d-flex flex-column ">
                <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between" style={{ minHeight: "5%" }}>
                  <h6 className="m-0 font-weight-bold">Jobs to Approve</h6>
                </div>

                {/* Card Body with Scrollable List */}
                <div className="card-body overflow-auto blue-color" style={{ flex: "1 1 0", minHeight: 0 }}>
                  {jobApprovals && jobApprovals.length !== 0 ? (
                    <>
                      {jobApprovals.map((jobApproval) => (
                        <div className="transaction-item" key={jobApproval.JobID}>
                          <div className="transaction-content">
                            <div className="transaction-item-margins">
                              <div className="description">
                                <span className="fw-bold">{jobApproval.ChildName}</span> completed {jobApproval.JobName}
                              </div>
                            </div>

                            <div className="transaction-item-margins">
                              <div className="amount positive">
                                ${jobApproval.PaymentAmount}
                              </div>
                            </div>

                            <div className="transaction-item-margins">
                              <div className="date">
                                {jobApproval.DateCompleted ? dateFormatter.format(new Date(jobApproval.DateCompleted)) : ""}
                              </div>
                            </div>

                            <div className="icon-buttons-container">
                              <button
                                className="icon-button"
                                onClick={(e) => handleApproveJob(e, jobApproval)}
                              >
                                <Tooltip
                                  content={<i className="fas fa-check fa-lg fa-fw mr-2 icon green" />}
                                  description="Approve"
                                  isCustom={true}
                                />
                              </button>
                              <button
                                className="icon-button"
                                onClick={(e) => handleRejectJob(e, jobApproval)}
                              >
                                <Tooltip
                                  content={<i className="fas fa-times fa-lg fa-fw mr-2 icon red" />}
                                  description="Reject"
                                  isCustom={true}
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <h4 className="text-muted text-center">No jobs pending approval</h4>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ChildActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        child={selectedChildForAction}
        onAddFunds={handleAddFunds}
        onSubtractFunds={handleSubtractFunds}
      />

      <AddFundsForm
        isOpen={isAddFundsOpen}
        onClose={() => { setIsAddFundsOpen(false); }}
        child={selectedChildForAction}
      />
      <SubtractFundsForm
        isOpen={isSubtractFundsOpen}
        onClose={() => { setIsSubtractFundsOpen(false); }}
        child={selectedChildForAction}
      />
      <AddJobsForm
        isOpen={isAddJobsOpen}
        onClose={() => { setIsAddJobsOpen(false); }}
      />
      <AccountInfoForm
        isOpen={isAccountInfoOpen}
        onClose={() => { setIsAccountInfoOpen(false); }}
      />
    </div >

  );
};

export default ParentDash;
